/* eslint-disable no-unused-vars */
import React, { useMemo, useState } from 'react';
import { useQuery } from 'react-query';
import { useMutation, useQueryClient } from 'react-query';
import {
  HeaderTitle,
  LoaderWrapper,
  StyledContainer,
  TableCell,
  TableCellEmail,
  TableWrapper,
  TwoColumns,
  IconButton,
} from '../../../services/settings/appStyle/global.style';
import { UserContainer } from './user.style';
import {
  Loader,
  Table,
  IconButton as IconButtonWithText,
  DropdownButton,
  DeleteConfirmationModal,
  TabButton,
} from '../../../Components/uielements';
import { useUserContext } from '../../../hooks/UserContext';
// import Company from '../../../services/bioplus-services/company';
import { AddPlusIcon, DeleteIcon } from '../../../Components/icons';
import { InviteUserModal /* , UserEditAndPermissionModal */ } from './modals';
// import { ownerOption } from './constants'; // needed if Edit is re-enabled below
import { IntlMessages } from '../../../helper/langHelper';
import CloudDownloadIcon from '@mui/icons-material/CloudDownload';
import SimCardDownloadIcon from '@mui/icons-material/SimCardDownload';
import { exportExcelFile } from '../../../helper/exportExcelHelper';
import moment from 'moment';
import { convertToUserTimeZoneDateTime } from '../../../helper/dataFormat';
import {
  sentUserInvitationsList,
  userList,
  userDeletion,
} from '../../../services/bioplus-services/user';
import GroupCreationModal from '../BusinessGroup/modals/GroupCreationModal';
import GroupIcon from '@mui/icons-material/Group';
import GroupAddIcon from '@mui/icons-material/GroupAdd';
import BusinessGroup from '../BusinessGroup';

const NON_DELETABLE_ROLES = ['ADMIN', 'PARTNER', 'VISITOR'];

function extractUserListData(dataArray, userTimeZone) {
  if (dataArray) {
    const extractedData = dataArray?.map((item) => {
      return {
        Name: `${item?.firstName ?? ''} ${item?.lastName ?? ''}`.trim(),
        email: item?.email,
        mobile: item?.phone,
        lastLogin: item?.lastLogin
          ? convertToUserTimeZoneDateTime(item?.lastLogin, userTimeZone)
          : 'N/A',
      };
    });
    return { bioplusUsers: extractedData };
  }
}

// Member List Component
const UserList = ({ companyId, canManage }) => {
  // const [editUser, setEditUser] = useState(); // needed if Edit is re-enabled below
  const [deleteUser, setDeleteUser] = useState();
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const userTimeZone = useMemo(() => Intl.DateTimeFormat().resolvedOptions().timeZone, []);

  const usersQuery = useQuery(['users', companyId, page, pageSize], () =>
    userList({ companyId, page, pageSize, userType: 'OWNER' })
  );
  const userTableData = usersQuery?.data?.data ?? [];

  const queryClient = useQueryClient();

  const deleteMutation = useMutation((data) => userDeletion(data?._id), {
    onSuccess: async () => {
      alert('Member deleted successfully.');
      await queryClient.invalidateQueries('users');
    },
  });

  const handleNextPage = () => {
    setPage((value) => value + 1);
  };
  const handlePreviousPage = () => {
    setPage((value) => value - 1);
  };

  // const handleClosed = () => {
  //   setEditUser(undefined);
  // };

  const handleDeleteUser = (data) => {
    deleteMutation.mutate(data);
    setDeleteUser(undefined);
  };

  const userColumns = useMemo(
    () => [
      {
        Header: <IntlMessages id="table.col.name" />,
        accessor: 'userName',
        Cell: (cellData) => (
          <TableCell>
            {`${cellData?.row?.original?.firstName ?? ''} ${
              cellData?.row?.original?.lastName ?? ''
            }`}
          </TableCell>
        ),
        width: '25%',
      },
      {
        Header: <IntlMessages id="table.col.email" />,
        accessor: 'email',
        width: '30%',
        Cell: (cellData) => (
          <TableCellEmail>{`${cellData?.row?.original?.email ?? ''}`}</TableCellEmail>
        ),
      },
      {
        Header: <IntlMessages id="table.col.mobile.no" />,
        accessor: 'phone',
        width: '20%',
        Cell: (cellData) => <TableCell>{`${cellData?.row?.original?.phone ?? ''}`}</TableCell>,
      },
      {
        Header: <IntlMessages id="table.col.last.login" />,
        accessor: 'lastLogin',
        width: '25%',
        Cell: (cellData) => (
          <TableCell>
            {cellData?.row?.original?.lastLogin
              ? `${convertToUserTimeZoneDateTime(cellData?.row?.original?.lastLogin, userTimeZone)}`
              : 'N/A'}
          </TableCell>
        ),
      },
      // {
      //   Header: <IntlMessages id="table.col.actions" defaultMessage="Actions" />,
      //   accessor: 'actions',
      //   isAction: true,
      // },
    ],
    [userTimeZone]
  );

  const actionList = canManage
    ? [
        // (data) => (
        //   <IconButton onClick={() => setEditUser(data)}>
        //     <EditIcon />
        //   </IconButton>
        // ),
        (data) => {
          const isDeleteDisabled = NON_DELETABLE_ROLES.includes(data?.role);
          return (
            <IconButton
              onClick={() => !isDeleteDisabled && setDeleteUser(data)}
              disabled={isDeleteDisabled}
              style={isDeleteDisabled ? { opacity: 0.4, cursor: 'not-allowed' } : undefined}
            >
              <DeleteIcon style={{ width: '19px', height: '19px' }} />
            </IconButton>
          );
        },
      ]
    : [];

  if (usersQuery.isLoading) {
    return (
      <LoaderWrapper>
        <Loader />
      </LoaderWrapper>
    );
  }

  return (
    <>
      <TableWrapper>
        <Table
          data={userTableData}
          columns={userColumns}
          hasFooter={true}
          // actions={actionList}
          page={page}
          pageSize={pageSize}
          prevPage={handlePreviousPage}
          nextPage={handleNextPage}
          total={usersQuery?.data?.total || 0}
        />
      </TableWrapper>
      {/* {editUser && editUser !== undefined && canManage && (
        <UserEditAndPermissionModal
          onClose={handleClosed}
          data={extractUserInfo(editUser)}
          userRole="owner"
          options={ownerOption}
          invalidateQuery="users"
        />
      )} */}

      {deleteUser && canManage && (
        <DeleteConfirmationModal
          onClose={() => {
            setDeleteUser(undefined);
          }}
          message={
            <IntlMessages
              id="delete.comfirmation.text.user"
              values={{ user: `${deleteUser?.firstName} ${deleteUser?.lastName}` }}
            />
          }
          onConfirm={() => handleDeleteUser(deleteUser)}
          isLoading={deleteMutation.isLoading}
        />
      )}
    </>
  );
};

// Sent Invitations Member List Component
const SentInvitationUserList = () => {
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const userTimeZone = useMemo(() => Intl.DateTimeFormat().resolvedOptions().timeZone, []);
  const invitationsQuery = useQuery(['sentUserInvitationsList', page, pageSize], () =>
    sentUserInvitationsList(page, pageSize)
  );
  const invitationsData = invitationsQuery?.data?.data ?? [];

  const handleNextPage = () => {
    setPage((value) => value + 1);
  };
  const handlePreviousPage = () => {
    setPage((value) => value - 1);
  };

  const invitationColumns = useMemo(
    () => [
      {
        Header: <IntlMessages id="table.col.email" />,
        accessor: 'email',
        width: '25%',
        Cell: (cellData) => <TableCellEmail>{`${cellData?.row?.original?.email}`}</TableCellEmail>,
      },
      {
        Header: <IntlMessages id="table.col.role" />,
        accessor: 'role',
        width: '25%',
      },
      {
        Header: <IntlMessages id="table.col.createdAt" />,
        accessor: 'createdAt',
        width: '25%',
        Cell: (cellData) => (
          <TableCell>
            {cellData.value ? moment(cellData.value).format('DD-MM-YYYY') : '--'}
          </TableCell>
        ),
      },

      {
        Header: <IntlMessages id="table.col.status" defaultMessage="Status" />,
        accessor: 'status',
        width: '25%',
      },
    ],
    [userTimeZone]
  );

  const actionList = [];

  if (invitationsQuery.isLoading) {
    return (
      <LoaderWrapper>
        <Loader />
      </LoaderWrapper>
    );
  }

  return (
    <TableWrapper>
      <Table
        data={invitationsData}
        columns={invitationColumns}
        actions={actionList}
        hasFooter={true}
        page={page}
        pageSize={pageSize}
        prevPage={handlePreviousPage}
        nextPage={handleNextPage}
        total={invitationsQuery?.data?.total || 0}
      />
    </TableWrapper>
  );
};

// Main Member Component
const User = () => {
  const userContext = useUserContext();
  const companyId = userContext?.company?.companyId?.id;
  const [isAddUserModal, setIsAddUserModal] = useState(false);
  const [selectedTab, setSelectedTab] = useState('Member List');
  const [isOpenGroupCreation, setIsOpenGroupCreation] = useState(false);
  const userTimeZone = useMemo(() => Intl.DateTimeFormat().resolvedOptions().timeZone, []);

  const allUsers = useQuery(['allUsers'], () =>
    userList({ mode: 'ALL', userType: 'OWNER', companyId: userContext?.company?.companyId?._id })
  );

  const handleClosedAddUserModal = () => {
    setIsAddUserModal(false);
  };

  const handleSelectedTab = (selectedTab) => {
    setSelectedTab(selectedTab);
  };

  const { userPermission } = userContext;
  const isUserAllowToManage = userPermission?.find((permission) => permission?.label === 'user');

  const isGroupAllowToManage = userPermission?.find((permission) => permission?.label === 'group');
  // Permission flags derived from the "group" permission object
  const canCreateGroup = !!isGroupAllowToManage?.isCreate;
  const canViewGroup = !!isGroupAllowToManage?.isView;

  // Permission flags derived from the "user" permission object
  const canManageUser = !!isUserAllowToManage?.isManage;
  const canDownloadReport = !!isUserAllowToManage?.isReport;
  const canCreateUser = !!isUserAllowToManage?.isCreate;

  const excelFormatData = extractUserListData(allUsers?.data, userTimeZone);
  const fileName = `Members-${moment().format('DD-MM-YYYY-hh.mm A')}.xlsx`;
  const fileNameCsv = `Members-${moment().format('DD-MM-YYYY-hh.mm A')}.csv`;
  const columnConfig = [
    { header: 'Name', key: 'Name', width: 30 },
    { header: 'Email', key: 'email', width: 30 },
    { header: 'Mobile No', key: 'mobile', width: 20 },
    { header: 'Last Login', key: 'lastLogin', width: 20 },
  ];

  const handleClickItem = (item) => {
    // Guard: even if triggered programmatically, block export without report permission
    if (!canDownloadReport) return;

    switch (item?.key) {
      case 'excel':
        exportExcelFile(excelFormatData, fileName, columnConfig);
        break;
      case 'pdf':
        break;
      case 'csv':
        exportExcelFile(excelFormatData, fileNameCsv, columnConfig);
        break;
      default:
        break;
    }
  };

  return (
    <StyledContainer>
      <UserContainer>
        <HeaderTitle>
          <IntlMessages id="user.module.user" />
        </HeaderTitle>
        <TwoColumns style={{ gap: '10px' }}>
          <div style={{ display: 'flex', gap: '10px' }}>
            {canCreateUser && selectedTab !== 'Business Groups' && (
              <IconButtonWithText
                text={<IntlMessages id="button.text.add.user" />}
                icon={<AddPlusIcon />}
                onClick={() => {
                  setIsAddUserModal(true);
                }}
              />
            )}
            <TabButton
              buttonArray={[
                { buttonText: 'Member List', textId: '1', icon: <GroupIcon /> },
                // { buttonText: 'Sent Invitations', textId: '2', icon: <GroupAddIcon /> },
                ...(canViewGroup
                  ? [
                      {
                        buttonText: 'Business Groups',
                        textId: '3',
                        icon: <GroupAddIcon />,
                      },
                    ]
                  : []),
              ]}
              onButtonClick={handleSelectedTab}
              selectedTab={selectedTab}
            />
            {selectedTab === 'Member List' && excelFormatData && canDownloadReport && (
              <DropdownButton
                noOfColoumns={1}
                buttonArray={[
                  { key: 'excel', title: 'Export as Excel', icon: <CloudDownloadIcon /> },
                  { key: 'csv', title: 'Export as CSV', icon: <SimCardDownloadIcon /> },
                ]}
                onClick={handleClickItem}
              />
            )}

            {selectedTab === 'Business Groups' && canCreateGroup && (
              <IconButtonWithText
                text={<IntlMessages id="button.label.group.creation" />}
                icon={<AddPlusIcon />}
                onClick={() => {
                  setIsOpenGroupCreation(true);
                }}
              />
            )}
          </div>
        </TwoColumns>
      </UserContainer>

      {selectedTab === 'Member List' ? (
        <UserList companyId={companyId} canManage={canManageUser} />
      ) : selectedTab === 'Business Groups' ? (
        <>
          <BusinessGroup />
        </>
      ) : (
        <SentInvitationUserList />
      )}

      {isAddUserModal && (
        <InviteUserModal onClose={handleClosedAddUserModal} companyId={companyId} />
      )}

      {isOpenGroupCreation && <GroupCreationModal onClose={() => setIsOpenGroupCreation(false)} />}
    </StyledContainer>
  );
};

export default User;
